import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { paymentsApi } from '@/services/payments.service';
import type {
  AdminPaymentRecord,
  PaymentDateBasis,
  PaymentListResult,
  PaymentsSummary,
  PaymentTab,
} from '@/types/payment.types';
import { downloadBlob } from '@/utils/download.util';
import { paymentExportRangeSchema } from '@/validators/payments.validator';

import { PAGE_SIZE, PAYMENTS_QUERY_KEY } from '../constants';

/** Which inline confirm-form is open in the detail drawer. */
export type PaymentActionForm = 'reject' | 'settle' | 'refund' | null;

/** Field-level errors on the export range, keyed by field name. */
export type PaymentRangeErrors = Partial<Record<'from' | 'to', string>>;

export interface UsePaymentsPageResult {
  records: AdminPaymentRecord[];
  total: number;
  page: number;
  totalPages: number;
  tab: PaymentTab;
  search: string;
  summary: PaymentsSummary | undefined;
  selected: AdminPaymentRecord | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  mutatingId: string | null;
  actionError: string | null;
  openForm: PaymentActionForm;
  draft: string;
  from: string;
  to: string;
  basis: PaymentDateBasis;
  isExporting: boolean;
  exportError: string | null;
  rangeErrors: PaymentRangeErrors;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  onChangeBasis: (value: PaymentDateBasis) => void;
  onExport: () => void;
  onChangeTab: (tab: PaymentTab) => void;
  onChangeSearch: (search: string) => void;
  onChangePage: (page: number) => void;
  onSelect: (record: AdminPaymentRecord) => void;
  onCloseDrawer: () => void;
  onOpenForm: (form: PaymentActionForm) => void;
  onDraftChange: (value: string) => void;
  onVerify: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSettle: (id: string, adminRef: string) => void;
  onRefundDeposit: (id: string, ref?: string) => void;
  /** Nudges whichever party is missing payout details. */
  onRequestPayoutDetails: (id: string) => void;
  /** Key of the payout field just copied, for the transient "Copied" label. */
  copiedKey: string | null;
  onCopy: (key: string, value: string) => void;
}

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong';

export const usePaymentsPage = (): UsePaymentsPageResult => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<PaymentTab>('submitted');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<PaymentActionForm>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [basis, setBasis] = useState<PaymentDateBasis>('raised');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [rangeErrors, setRangeErrors] = useState<PaymentRangeErrors>({});

  const debouncedSearch = useDebouncedValue(search, 300);

  // The range narrows the table as well as the file, so an admin exports exactly what they see.
  const listQuery = useQuery<PaymentListResult>({
    queryKey: [PAYMENTS_QUERY_KEY, 'list', { tab, search: debouncedSearch, page, from, to, basis }],
    queryFn: () =>
      paymentsApi.list({
        tab,
        search: debouncedSearch || undefined,
        from: from || undefined,
        to: to || undefined,
        basis,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (previous) => previous,
  });

  const summaryQuery = useQuery<PaymentsSummary>({
    queryKey: [PAYMENTS_QUERY_KEY, 'summary'],
    queryFn: () => paymentsApi.summary(),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
  };

  const runMutation = (
    id: string,
    fn: () => Promise<AdminPaymentRecord | null>,
  ): void => {
    setActionError(null);
    setMutatingId(id);
    fn()
      .catch((error: unknown) => setActionError(messageOf(error)))
      .finally(() => {
        setMutatingId(null);
        invalidate();
      });
  };

  const verifyMutation = useMutation({ mutationFn: (id: string) => paymentsApi.verify(id) });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => paymentsApi.reject(id, reason),
  });
  const settleMutation = useMutation({
    mutationFn: ({ id, adminRef }: { id: string; adminRef: string }) =>
      paymentsApi.settle(id, adminRef),
  });
  const refundMutation = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref?: string }) => paymentsApi.refundDeposit(id, ref),
  });
  const requestPayoutMutation = useMutation({
    mutationFn: (id: string) => paymentsApi.requestPayoutDetails(id),
  });

  // The list DTO carries an empty `bookingTimeline` — only the detail endpoint fills it in.
  // Rendering the drawer straight off the list row is why booking phases never showed up.
  const detailQuery = useQuery<AdminPaymentRecord>({
    queryKey: [PAYMENTS_QUERY_KEY, 'detail', selectedId],
    queryFn: () => paymentsApi.get(selectedId as string),
    enabled: selectedId !== null,
  });

  const records = listQuery.data?.items ?? [];
  const listRow = selectedId ? records.find((r) => r.id === selectedId) ?? null : null;
  // Fall back to the list row while the detail request is in flight so the drawer opens instantly.
  const selected = selectedId ? detailQuery.data ?? listRow : null;

  const resetForm = (): void => {
    setOpenForm(null);
    setDraft('');
  };

  const runExport = (): void => {
    const parsed = paymentExportRangeSchema.safeParse({ from, to, basis });
    if (!parsed.success) {
      const issues: PaymentRangeErrors = {};
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];
        if ((field === 'from' || field === 'to') && !issues[field]) issues[field] = issue.message;
      });
      setRangeErrors(issues);
      setExportError(null);
      return;
    }

    setRangeErrors({});
    setExportError(null);
    setIsExporting(true);
    paymentsApi
      .exportXlsx({
        ...parsed.data,
        tab,
        search: debouncedSearch || undefined,
      })
      .then((blob) => downloadBlob(blob, `shoutt-payments-${parsed.data.from}_${parsed.data.to}.xlsx`))
      .catch((error: unknown) => setExportError(messageOf(error)))
      .finally(() => setIsExporting(false));
  };

  return {
    records,
    total: listQuery.data?.total ?? 0,
    page: listQuery.data?.page ?? page,
    totalPages: listQuery.data?.totalPages ?? 1,
    tab,
    search,
    summary: summaryQuery.data,
    selected,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    mutatingId,
    actionError,
    onChangeTab: (next) => {
      setTab(next);
      setPage(1);
    },
    onChangeSearch: (next) => {
      setSearch(next);
      setPage(1);
    },
    onChangePage: setPage,
    openForm,
    draft,
    from,
    to,
    basis,
    isExporting,
    exportError,
    rangeErrors,
    onChangeFrom: (value) => {
      setFrom(value);
      setRangeErrors((current) => ({ ...current, from: undefined }));
      setPage(1);
    },
    onChangeTo: (value) => {
      setTo(value);
      setRangeErrors((current) => ({ ...current, to: undefined }));
      setPage(1);
    },
    onChangeBasis: (value) => {
      setBasis(value);
      setPage(1);
    },
    onExport: runExport,
    onSelect: (record) => {
      setActionError(null);
      resetForm();
      setSelectedId(record.id);
    },
    onCloseDrawer: () => {
      setSelectedId(null);
      resetForm();
    },
    onOpenForm: (form) => {
      setActionError(null);
      setDraft('');
      setOpenForm(form);
    },
    onDraftChange: setDraft,
    onVerify: (id) => runMutation(id, () => verifyMutation.mutateAsync(id)),
    onReject: (id, reason) => {
      resetForm();
      runMutation(id, () => rejectMutation.mutateAsync({ id, reason }));
    },
    onSettle: (id, adminRef) => {
      resetForm();
      runMutation(id, () => settleMutation.mutateAsync({ id, adminRef }));
    },
    onRefundDeposit: (id, ref) => {
      resetForm();
      runMutation(id, () => refundMutation.mutateAsync({ id, ref }));
    },
    onRequestPayoutDetails: (id) => {
      runMutation(id, () => requestPayoutMutation.mutateAsync(id));
    },
    copiedKey,
    onCopy: (key, value) => {
      // Clipboard access is denied on insecure origins and by some policies; the admin can still
      // select the text by hand, so a failure just leaves the label unchanged.
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopiedKey(key);
          window.setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
        })
        .catch(() => setActionError('Could not copy to the clipboard'));
    },
  };
};
